import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      toast.success(t.contact.successTitle, { description: t.contact.successBody });
      setDone(true);
      setForm({ first_name: "", last_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.errorTitle, { description: t.contact.errorBody });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="pt-20 md:pt-28 pb-12">
        <div className="container-x">
          <div className="eyebrow">— {t.contact.eyebrow}</div>
          <h1 className="mt-6 text-[clamp(2.5rem,6vw,5.5rem)] leading-[1.02] tracking-[-0.02em] max-w-4xl">
            {t.contact.title}
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
            {t.contact.lead}
          </p>
        </div>
      </section>

      <section className="container-x pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* FORM */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="border-t border-[hsl(var(--line))] pt-8">
              <div className="eyebrow">— Form</div>
              <h2 className="mt-3 text-2xl md:text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t.contact.formTitle}
              </h2>
            </div>

            {done ? (
              <div
                data-testid="contact-success"
                className="mt-10 border border-[hsl(var(--ocre))] bg-[hsl(var(--bg-elev))] p-10"
              >
                <div className="display text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t.contact.successTitle}
                </div>
                <p className="mt-3 text-[hsl(var(--ink-soft))]">{t.contact.successBody}</p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-6 link-underline text-sm uppercase tracking-[0.18em]"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label={t.contact.firstName}
                    id="first_name"
                    required
                    value={form.first_name}
                    onChange={onChange("first_name")}
                  />
                  <Field
                    label={t.contact.lastName}
                    id="last_name"
                    required
                    value={form.last_name}
                    onChange={onChange("last_name")}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label={t.contact.email}
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                  />
                  <Field
                    label={t.contact.phone}
                    id="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={onChange("phone")}
                  />
                </div>
                <div>
                  <Label
                    htmlFor="message"
                    className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
                  >
                    {t.contact.message}
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="field-message"
                    required
                    rows={6}
                    placeholder={t.contact.messagePlaceholder}
                    value={form.message}
                    onChange={onChange("message")}
                    className="mt-3 border-0 border-b border-[hsl(var(--line))] rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[hsl(var(--ink))] placeholder:text-[hsl(var(--muted-foreground))]/60 text-base"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {loading ? t.contact.sending : t.contact.submit}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>

          {/* INFO */}
          <aside className="lg:col-span-4 lg:col-start-9 order-1 lg:order-2">
            <div className="border-t border-[hsl(var(--line))] pt-8">
              <div className="eyebrow">— {t.contact.infoTitle}</div>
            </div>
            <div className="mt-8 space-y-8 text-sm">
              <InfoBlock icon={<MapPin className="h-4 w-4" />} label="Address">
                <span className="whitespace-pre-line">{t.contact.infoAddress}</span>
              </InfoBlock>
              <InfoBlock icon={<Phone className="h-4 w-4" />} label="Phone">
                <a href="tel:+393662689583" className="link-underline" data-testid="contact-phone">
                  {t.contact.infoPhone}
                </a>
              </InfoBlock>
              <InfoBlock icon={<Mail className="h-4 w-4" />} label="Email">
                <a href="mailto:contact@esra_srls.com" className="link-underline block" data-testid="contact-email">
                  {t.contact.infoEmail}
                </a>
                <a
                  href="mailto:12484970012@impresa.italia.it"
                  className="link-underline block text-[hsl(var(--muted-foreground))] text-xs mt-1"
                  data-testid="contact-pec"
                >
                  PEC · {t.contact.infoPec}
                </a>
              </InfoBlock>
              <InfoBlock icon={<Clock className="h-4 w-4" />} label={t.contact.hoursTitle}>
                <span className="whitespace-pre-line">{t.contact.hoursBody}</span>
              </InfoBlock>
            </div>
          </aside>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[hsl(var(--bg-elev))] pb-0">
        <div className="container-x pt-20 md:pt-24 pb-10">
          <div className="eyebrow">— Map</div>
          <h2 className="mt-3 text-3xl md:text-5xl leading-[1.05]">
            {t.contact.mapTitle}
          </h2>
        </div>
        <div className="w-full h-[60vh] md:h-[70vh]" data-testid="contact-map">
          <iframe
            title="ESRA S.R.L.S. — Turin map"
            src="https://www.google.com/maps?q=Via+Sigismondo+Gerdil+5%2FB%2C+10152+Torino+TO%2C+Italia&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(0.25) contrast(1.05)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, type = "text", required, value, onChange }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={`field-${id}`}
        className="mt-3 border-0 border-b border-[hsl(var(--line))] rounded-none bg-transparent px-0 h-12 focus-visible:ring-0 focus-visible:border-[hsl(var(--ink))] text-base"
      />
    </div>
  );
}

function InfoBlock({ icon, label, children }) {
  return (
    <div className="flex gap-4">
      <div className="mt-1 text-[hsl(var(--ocre-deep))]">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{label}</div>
        <div className="mt-2 text-[hsl(var(--ink))] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
